import { Crown, Medal, Trophy } from 'lucide-preact';
import type { LeaderboardEntry, LeaderboardId } from '@vm/shared';
import { useState } from 'preact/hooks';
import { api } from '../api/routes';
import { replace, route } from '../state/nav';
import { me } from '../state/store';
import { fmtNum } from '../ui/format';
import { useAsync } from '../ui/hooks';
import { Avatar, Empty, ErrorState, OnlineOnly, Screen, Segmented, Spinner, Tabs } from '../ui/kit';
import { openProfile } from './Profile';

const BOARDS: { id: LeaderboardId; label: string; unit: string }[] = [
  { id: 'rating', label: 'Rating', unit: 'rating' },
  { id: 'level', label: 'Level', unit: 'level' },
  { id: 'kills', label: 'Takedowns', unit: 'takedowns' },
  { id: 'wins', label: 'Wins', unit: 'wins' },
  { id: 'voxels', label: 'Voxels', unit: 'voxels smashed' },
  { id: 'clans', label: 'Clans', unit: 'clan XP' },
];

function RankBadge(props: { rank: number }) {
  if (props.rank <= 3)
    return (
      <span class={`rank-medal m${props.rank}`}>
        {props.rank === 1 ? <Crown size={18} strokeWidth={3} /> : <Medal size={18} strokeWidth={3} />}
        <b>{props.rank}</b>
      </span>
    );
  return <span class="rank-num">{props.rank}</span>;
}

function Row(props: { e: LeaderboardEntry; board: LeaderboardId; mine?: boolean }) {
  const e = props.e;
  const clickable = props.board !== 'clans';
  return (
    <button class={`lb-row ${props.mine ? 'is-me' : ''} ${e.rank <= 3 ? `top${e.rank}` : ''}`} disabled={!clickable} onClick={() => clickable && openProfile(e.id, e.name)}>
      <RankBadge rank={e.rank} />
      <Avatar name={e.name} size={36} />
      <span class="lb-row-name">
        {e.clanTag && <span class="frow-tag">[{e.clanTag}]</span>}
        {e.name}
      </span>
      <b class="lb-row-value">{fmtNum(e.value)}</b>
    </button>
  );
}

export function LeaderboardsScreen() {
  const board = ((route.value.params.board as LeaderboardId) || 'rating') as LeaderboardId;
  const [period, setPeriod] = useState<'week' | 'all'>('week');
  const res = useAsync(() => api.leaderboards.get(board, period), [board, period]);
  const myId = me.value?.id;
  const def = BOARDS.find((b) => b.id === board) ?? BOARDS[0];
  const mineInList = res.data?.entries.some((e) => e.id === myId || (board === 'clans' && e.id === me.value?.clan?.id));
  return (
    <Screen
      title="Leaderboards"
      icon={<Trophy size={26} />}
      tabs={<Tabs<LeaderboardId> value={board} onChange={(b) => replace('leaderboards', { board: b })} tabs={BOARDS.map((b) => ({ id: b.id, label: b.label }))} />}
    >
      <OnlineOnly feature="Leaderboards">
        <div class="scr-scroll narrow">
          <div class="lb-controls">
            <Segmented
              options={[
                { value: 'week', label: 'This week' },
                { value: 'all', label: 'All time' },
              ]}
              value={period}
              onChange={setPeriod}
            />
            <span class="muted">Ranked by {def.unit}</span>
          </div>
          {res.loading && !res.data ? (
            <Spinner label="Loading rankings…" />
          ) : res.error ? (
            <ErrorState message={res.error} onRetry={res.reload} />
          ) : !res.data?.entries.length ? (
            <Empty icon={<Trophy size={44} />} title="No rankings yet" body="Play some matches to get on the board!" />
          ) : (
            <div class="lb-list" key={`${board}:${period}`}>
              {res.data.entries.map((e) => (
                <Row key={e.id} e={e} board={board} mine={e.id === myId || (board === 'clans' && e.id === me.value?.clan?.id)} />
              ))}
            </div>
          )}
          {res.data?.me && !mineInList && (
            <div class="lb-me">
              <Row e={res.data.me} board={board} mine />
            </div>
          )}
        </div>
      </OnlineOnly>
    </Screen>
  );
}
